import { RefObject, useEffect } from "react";

/**
 * Props for the useKeyPress hook
 */
export type useKeyPressProps = {
	/**
	 * The key to listen for (e.g. "Enter", "Escape", "a")
	 */
	key: string;
	/**
	 * Callback function to execute when the key is pressed
	 * @param e - The keyboard event object
	 */
	onPress: (e: KeyboardEvent) => void;
	/**
	 * Whether the key press handler should be disabled
	 * @default false
	 */
	disabled?: boolean;
	/**
	 * Optional ref to a container element. If provided, the key press will only trigger
	 * when the container or its children have focus
	 */
	containerRef?: RefObject<HTMLElement>;
	/**
	 * The keyboard event type to listen for
	 * @default "keydown"
	 */
	event?: "keydown" | "keyup";
};

/**
 * This hook listens for a key press event and executes a callback function.
 * It can be used to handle keyboard shortcuts or other key presses.
 * and can isolate the keyboard press to a specific container element.
 *
 * @param event - The keyboard event type to listen for
 * @param key - The key to listen for
 * @param onPress - The callback function to execute when the key is pressed
 * @param disabled - Whether the key press handler should be disabled
 */
export const useKeyPress = ({ event = "keydown", key, onPress, disabled = false, containerRef }: useKeyPressProps) => {
	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === key && !disabled) {
				if (containerRef?.current) {
					const activeElement = containerRef.current.querySelector(":focus");
					if (activeElement) {
						onPress(e);
					}
				} else {
					onPress(e);
				}
			}
		};

		window.addEventListener(event, handleKeyDown);
		return () => window.removeEventListener(event, handleKeyDown);
	}, [event, key, onPress, disabled, containerRef]);
};
